import { Modal, ModalBody, ModalContent, ModalOverlay } from "@chakra-ui/react";
import { useCallback, useContext, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { fetchSlotsAsync, selectAppState, updateBreadcrumbValue } from "../../app/slice/appSlice";
import useConditions from "../../hooks/useConditions";
import useUnsavedBar from "../../hooks/useUnsavedBar";
import { LoggerContext } from "@reconvert/react-logger";
import { selectCondition, selectConditionAsync } from "../../app/slice/conditionSlice";
import { generateBreadcrumbFormat } from "../../utils/helpers";
import i18n from "../../utils/i18n";

const IFrameComponent = () => {
  const { iFrameLink, isAppBridgePresent, platformStoreId, activePage } = useAppSelector(selectAppState);
  const { handleDiscard, handleSave } = useUnsavedBar();
  const { clostIFrame, selectedCondition } = useConditions();
  const { logger } = useContext(LoggerContext);
  const dispatch = useAppDispatch();

  const handleIFrameMessage = useCallback(
    (e: MessageEvent<any>) => {
      if (e.data.eventType === "exit" && e.data.data === true) {
        if (selectedCondition?.id) {
          // If the condition is selected, we need to update with latest condition
          // and update the breadcrumb value with latest condition name and location
          dispatch(selectCondition(selectedCondition?.id));
          dispatch(selectConditionAsync(selectedCondition?.id))
            .unwrap()
            .then((res: any) => {
              dispatch(
                updateBreadcrumbValue({
                  breadCrumb: generateBreadcrumbFormat({ page: res?.data?.location, condition: res?.data?.name }),
                }),
              );
              clostIFrame();
            });
          dispatch(fetchSlotsAsync());
        }

        return;
      }

      if (e.data.event === "save-form" && e.data.isInlineChange) {
        handleSave();
        return;
      }

      if (e.data.event === "discard-form" && e.data.isInlineChange) {
        handleDiscard();
        return;
      }

      if (e?.data?.page === "flow-editor") {
        const editorIframe = document.getElementById("checkout-editor-iframe");

        if (editorIframe) {
          const editorIframeWindow = (editorIframe as any)?.contentWindow;

          if (editorIframeWindow) {
            editorIframeWindow.postMessage(
              {
                ...e.data,
              },
              "*",
            );
          } else {
            console.error("Failed to get contentWindow of the iframe.");
          }
        }
      } else if (window.opener && window.top !== window) {
        window.opener.postMessage(
          {
            ...e.data,
          },
          "*",
        );
      }
    },
    [clostIFrame, dispatch, handleDiscard, handleSave, selectedCondition?.id, activePage],
  );

  useEffect(() => {
    window.addEventListener("message", handleIFrameMessage);

    return () => {
      window.removeEventListener("message", handleIFrameMessage);
    };
  }, [handleIFrameMessage]);

  if (isAppBridgePresent) {
    logger.info(`sending message to open flow editor :: ${platformStoreId}`, iFrameLink);
    if (iFrameLink) {
      postMessage(
        {
          eventType: "open-flow-editor",
          data: { url: `${iFrameLink}&fromCondition=true&language=${i18n.language}` },
        },
        "*",
      );
    }

    return null;
  }

  return (
    <Modal trapFocus={false} onClose={clostIFrame} size={"full"} isOpen={!!iFrameLink} scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalBody w="100%" h="100%" m={0}>
          <iframe
            title="Checkout Editor Page"
            id="checkout-editor-iframe"
            src={iFrameLink + "&fromCondition=true&isAdmin=true"}
            allowFullScreen
            style={{ width: "100%", height: "100vh" }}
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default IFrameComponent;
